import React, { useEffect, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { usePatchForm } from '../../providers/PatchFormProvider';
import Select from 'react-select';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import { toBase64 } from '../../common/Utils/base64';
import api from '../../components/services/apiService';
import { ENABLE_PATCH_ACTION_CUSTOM_SCRIPT } from '../../common/config/featureFlags';

interface Script {
  scriptName: string;
  tenantScriptStoreId: string;
  scriptContent: string;
  scriptDescription: string;
}

const RunScript = () => {
  const { patchFormData, setPatchFormData } = usePatchForm();

  const [tenantScript, setTenantScript] = useState([]);

  // ✅ unified state: only using selectScript, no separate "select"
  const [selectScript, setSelectScript] = useState({
    ActionType: 'Script',
    WhenToExecute: 'PostRemediation',
    TenantScriptStoreId: '' as string | null,
    ScriptContent: '',
    ScriptName: '',
    ScriptDescription: '',
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'choose' | 'custom'>('choose');
  const [customScript, setCustomScript] = useState('');
  const [apply, setApply] = useState(false);

  const auth = useAuth();
  const { selectedTenantId } = useTenantSelection();
  // const [scripts, setScripts] = useState<Script[]>([]);

  // const getTenentGroupsScripts = async () => {
  //   try {
  //     if (auth && auth.userData) {
  //       const token = auth.userData.access_token;
  //       //let tenantId = 'D64341A6-07F2-4F2C-93BC-339C81874565';
  //       //const tenantId = Project.tenantId;
  //       let tenantId = auth?.userData?.profile.tenant_id;
  //       const response = await axios.get(`${Project.api}tenantservice/Tenant/${tenantId}/scripts`, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //         },
  //       });

  //       const { data } = response.data;

  //       if (Array.isArray(data)) {
  //         setScripts(data);
  //       } else {
  //         console.warn('Expected array but got:', data);
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Error fetching scripts:', error);
  //   }
  // };

  // useEffect(() => {
  //   getTenentGroupsScripts();
  // }, []);

  const handleScriptSelection = (scriptName: string, scriptId: string, description: string) => {
    setSelectScript((prev) => ({
      ...prev,
      ScriptName: scriptName,
      TenantScriptStoreId: scriptId,
      ScriptContent: '',
      ScriptDescription: description,
    }));
  };

  const applyScript = () => {
    setApply(true);
    let finalScript = { ...selectScript };
    if (activeTab === 'custom') {
      finalScript.ScriptContent = toBase64(customScript);
      finalScript.ScriptName = 'Custom Script';
      finalScript.ScriptDescription = 'Manually entered script';
      finalScript.TenantScriptStoreId = null;
    } else {
      finalScript.ScriptContent = finalScript.ScriptContent;
    }

    if (!finalScript.WhenToExecute) {
      finalScript.WhenToExecute = 'PostRemediation';
    }

    setPatchFormData((prev) => {
      const existingList = prev.RemediationPlanActionList ?? [];

      // ✅ Remove existing entry with the same TenantScriptStoreId
      const filteredList = existingList.filter(
        (action) => !(action.ActionType === 'Script' && action.TenantScriptStoreId === finalScript.TenantScriptStoreId),
      );

      return {
        ...prev,
        RemediationPlanActionList: [...filteredList, finalScript],
      };
    });

    setIsExpanded(false);
  };

  const customStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: '#333333',
      borderColor: '#555555',
      color: '#ffffff',
      boxShadow: 'none',
      '&:hover': { borderColor: '#777777' },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#ffffff',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#aaaaaa',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#2a2a2a',
      border: '1px solid #555555',
      borderRadius: '4px',
      overflow: 'hidden',
      zIndex: 9999,
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: 200,
      overflowY: 'auto',
      padding: 0,
      '::-webkit-scrollbar': { width: '6px' },
      '::-webkit-scrollbar-thumb': { background: '#555', borderRadius: '3px' },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2563EB' : state.isFocused ? '#383838' : 'transparent',
      color: '#ffffff',
      cursor: 'pointer',
      padding: '10px',
    }),
  };

  // const options =
  //   scripts?.map((item) => ({
  //     label: item.scriptName,
  //     value: item.tenantScriptStoreId,
  //     content: item.scriptContent,
  //     description: item.scriptDescription,
  //   })) ?? [];

  // Merge scripts from both APIs
  const options = [
    // ...(scripts?.map((item) => ({
    //   label: item.scriptName,
    //   value: item.tenantScriptStoreId ?? item.tenantScriptStoreID, // handle both naming cases
    //   content: item.scriptContent ?? '',
    //   description: item.scriptDescription ?? '',
    // })) ?? []),
    ...(tenantScript?.map((item) => ({
      label: item.scriptName,
      value: item.tenantScriptStoreId ?? item.tenantScriptStoreID,
      content: item.scriptContent ?? '',
      description: item.scriptDescription ?? '',
      isGlobalScript: item.isGlobalScript ?? false,
    })) ?? []),
  ];

  const [searchInput, setSearchInput] = useState('');

  const selectedOption = options.find((opt) => opt.value === selectScript.TenantScriptStoreId) || null;

  const handleSelectChange = (selected: any) => {
    if (selected) {
      handleScriptSelection(selected.label, selected.value, selected.description);
    }
  };

  useEffect(() => {
    if (patchFormData?.RemediationPlanActionList) {
      const savedScript = patchFormData.RemediationPlanActionList.find((action) => action.ActionType === 'Script');
      if (savedScript) {
        setSelectScript(savedScript);
        setCustomScript(savedScript.ScriptName === 'Custom Script' ? savedScript.ScriptContent : '');
        setApply(true);
      }
    }
  }, [patchFormData]);

  useEffect(() => {
    if (selectScript.TenantScriptStoreId || selectScript.ScriptContent) {
      setPatchFormData((prev) => {
        const updatedList = (prev.RemediationPlanActionList ?? []).filter((a) => a.ActionType !== 'Script');
        return {
          ...prev,
          RemediationPlanActionList: [...updatedList, selectScript],
        };
      });
    }
  }, [selectScript]);

  const getScriptAutomation = async () => {
    try {
      if (auth && auth.userData) {
        const token = auth.userData.access_token;
        let tenantId = selectedTenantId;

        const response = await api.get(`${Project.api}tenantservice/TenantScript/${tenantId}/tenant-script`);
        let { data } = response;
        setTenantScript(data.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    getScriptAutomation();
  }, []);

  return (
    <div className={`bg-[#1e1e1e] text-white p-4 rounded-md border ${apply ? 'border-green-500' : 'border-gray-600'}`}>
      <div
        className="flex justify-between items-center cursor-pointer p-3 bg-[#2a2a2a] rounded-md"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span>Run Script</span>
        {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
      </div>

      {isExpanded && (
        <div className="mt-2 bg-[#2a2a2a] border border-gray-600 rounded-md p-3">
          <div className="flex">
            <button
              className={`px-4 py-2 ${activeTab === 'choose' ? 'bg-[#333333]' : 'bg-[#252525]'} rounded-md mr-2`}
              onClick={() => setActiveTab('choose')}
            >
              Choose Script
            </button>
            {ENABLE_PATCH_ACTION_CUSTOM_SCRIPT ? (
              <button
                className={`px-4 py-2 ${activeTab === 'custom' ? 'bg-[#333333]' : 'bg-[#252525]'} rounded-md`}
                onClick={() => setActiveTab('custom')}
              >
                Enter Custom Script
              </button>
            ) : (
              <></>
            )}
          </div>

          {activeTab === 'choose' ? (
            <div className="mt-3">
              <Select
                options={options}
                value={selectedOption}
                onChange={handleSelectChange}
                styles={{
                  ...customStyles,
                  input: (provided) => ({
                    ...provided,
                    color: '#ffffff', // make typed text visible
                  }),
                }}
                placeholder="Select a script"
                isSearchable
                menuPortalTarget={document.body}
                inputValue={searchInput}
                onInputChange={(value) => setSearchInput(value)}
              />
            </div>
          ) : (
            <div className="mt-3">
              <input
                type="text"
                value={customScript}
                onChange={(e) => setCustomScript(e.target.value)}
                className="w-full p-2 bg-[#333333] rounded-md text-white border border-gray-500"
                placeholder="Enter custom script"
              />
            </div>
          )}

          {/* Radio Button Options */}
          <div className="flex items-center gap-x-6 mt-5">
            <label className="flex items-center gap-x-2">
              <input
                type="radio"
                name="rebootOption"
                value="PostRemediation"
                checked={selectScript.WhenToExecute === 'PostRemediation'}
                onChange={(e) =>
                  setSelectScript((prev) => ({
                    ...prev,
                    WhenToExecute: e.target.value,
                  }))
                }
              />
              <span>Postpatching</span>
            </label>
            <label className="flex items-center gap-x-2">
              <input
                type="radio"
                name="rebootOption"
                value="PreRemediation"
                checked={selectScript.WhenToExecute === 'PreRemediation'}
                onChange={(e) =>
                  setSelectScript((prev) => ({
                    ...prev,
                    WhenToExecute: e.target.value,
                  }))
                }
              />
              <span>Prepatching</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="mt-3 flex justify-start gap-3">
            <button
              className="px-4 py-2 border border-gray-400 rounded-[30px]"
              onClick={() => {
                setApply(false);
                setIsExpanded(false);
              }}
            >
              Clear
            </button>
            <button className="px-4 py-2 bg-red-600 text-white rounded-[30px]" onClick={applyScript}>
              Apply
            </button>
            {/* <button className="px-4 py-2 text-gray-400">Skip</button> */}
          </div>
        </div>
      )}
    </div>
  );
};

export default RunScript;
